import { ConditionChild, ConditionGroup, PersonalizationFieldGroup } from '../types';

export const personalizationFieldKey = (element: ConditionChild) => `${element.type}:${element.id}`;

export type PersonalizationFieldSection =
  | { element: ConditionChild; group?: never }
  | { group: PersonalizationFieldGroup; elements: ConditionChild[] };

export function getPersonalizationFieldGroups(tree: ConditionGroup): PersonalizationFieldGroup[] {
  return Array.isArray(tree.fieldGroups)
    ? tree.fieldGroups
        .filter(
          (group) =>
            group &&
            typeof group.childrenKey === 'string' &&
            typeof group.id === 'string' &&
            typeof group.name === 'string' &&
            Array.isArray(group.elements),
        )
        .map((group) => ({
          ...group,
          elements: group.elements.filter(
            (element) =>
              element &&
              (element.type === 'condition'
                ? typeof element.id === 'string'
                : ['text', 'image', 'addon', 'line', 'rectangle'].includes(element.type) &&
                  typeof element.id === 'number'),
          ),
        }))
    : [];
}

export function groupPersonalizationFields(
  tree: ConditionGroup,
  elements: ConditionChild[],
  childrenKey: string,
): PersonalizationFieldSection[] {
  const owner = new Map<string, PersonalizationFieldGroup>();
  getPersonalizationFieldGroups(tree).forEach((group) => {
    if (group.childrenKey === childrenKey) {
      group.elements.forEach((element) => {
        if (element && !owner.has(personalizationFieldKey(element))) {
          owner.set(personalizationFieldKey(element), group);
        }
      });
    }
  });
  const result: PersonalizationFieldSection[] = [];
  const sections = new Map<PersonalizationFieldGroup, ConditionChild[]>();
  elements.forEach((element) => {
    const group = owner.get(personalizationFieldKey(element));
    if (!group) {
      result.push({ element });
      return;
    }
    let members = sections.get(group);
    if (!members) {
      members = [];
      sections.set(group, members);
      result.push({ group, elements: members });
    }
    members.push(element);
  });
  return result;
}

export function savePersonalizationFieldGroup(tree: ConditionGroup, group: PersonalizationFieldGroup) {
  const available = tree.children[group.childrenKey];
  if (!available || !group.id || !group.name.trim()) {
    return;
  }
  const selected = new Set(group.elements.map(personalizationFieldKey));
  const elements = available.filter((element) => selected.has(personalizationFieldKey(element)));
  const next = { ...group, name: group.name.trim(), elements };
  const groups = getPersonalizationFieldGroups(tree);
  const exists = groups.some((item) => item.id === group.id);
  const updated = groups.map((item) => {
    if (item.id === group.id) {
      return next;
    }
    return item.childrenKey === group.childrenKey
      ? { ...item, elements: item.elements.filter((element) => !selected.has(personalizationFieldKey(element))) }
      : item;
  });
  if (!exists) {
    updated.push(next);
  }
  tree.fieldGroups = updated.filter((item) => item.elements.length > 0);
  if (!tree.fieldGroups.length) {
    delete tree.fieldGroups;
  }
}
